import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Layouts
import Layout from './components/Layout';
import ModuleLayout from './components/ModuleLayout';
import ErrorBoundary from './components/ErrorBoundary';
import DashboardRouter from './components/DashboardRouter';

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Login from './pages/Login';
import ModuleHome from './pages/ModuleHome';
import Dashboard from './pages/Dashboard';
import Alerts from './pages/Alerts';
import AlertDetail from './pages/AlertDetail';
import Monitors from './pages/Monitors';
import Movimientos from './pages/Movimientos';
import Padron from './pages/Padron';
import ClienteDetalle from './pages/ClienteDetalle';
import Audit from './pages/Audit';
import UsersPage from './pages/Users';
import Reports from './pages/Reports';
import RiskScoring from './pages/RiskScoring';
import AlertsCalendar from './pages/AlertsCalendar';
import AccountLists from './pages/AccountLists';
import MonitorTemplates from './pages/MonitorTemplates';
import EscalationConfig from './pages/EscalationConfig';
import EscalationConfigV2 from './pages/EscalationConfigV2';
import MRCConfig from './pages/MRCConfig';
import ExecutiveDashboard from './pages/ExecutiveDashboard';
import CasesPage from './pages/Cases';
import CasesKanban from './pages/CasesKanban';
import LinkAnalysisPage from './pages/LinkAnalysis';
import GeoHeatmap from './pages/GeoHeatmap';
import FieldConfigPage from './pages/FieldConfig';
import ModuleAdmin from './pages/ModuleAdmin';
import DashboardConfig from './pages/DashboardConfig';
import CaseStatusesAdmin from './pages/CaseStatusesAdmin';
import CaseDuplicates from './pages/CaseDuplicates';
import BacklogCleanup from './pages/BacklogCleanup';
import PrepararVigilancia from './pages/PrepararVigilancia';
import SystemSettings from './pages/SystemSettings';
import EmpresasPage from './pages/Empresas';
import EmpresaDetalle from './pages/EmpresaDetalle';
import CrearSucursal from './pages/CrearSucursal';
import CuentasWaya from './pages/CuentasWaya';
import CoelsaTransacciones from './pages/CoelsaTransacciones';
import WayaMovimientos from './pages/WayaMovimientos';
import CasoDetalle from './pages/CasoDetalle';
import InformeEditor from './pages/InformeEditor';
import AnalisisVinculacion from './pages/AnalisisVinculacion';
import GruposUsuarios from './pages/GruposUsuarios';
import PlantillasCaso from './pages/PlantillasCaso';
import Aprobaciones from './pages/Aprobaciones';
import FlujosAprobacionV2 from './pages/FlujosAprobacionV2';
import OnboardingConfig from './pages/OnboardingConfig';
import OnboardingPortal from './pages/OnboardingPortal';
import SolicitudesOnboarding from './pages/SolicitudesOnboarding';
import OnboardingReview from './pages/OnboardingReview';
import Delegaciones from './pages/Delegaciones';
import HistorialActividad from './pages/HistorialActividad';
import NotificacionesHome from './pages/NotificacionesHome';
// PermisosConfig eliminado - funcionalidad integrada en GestionUsuarios
import NotificacionesInbox from './pages/NotificacionesInbox';
import NotificacionesAdmin from './pages/NotificacionesAdmin';
import NotificacionesSuscripciones from './pages/NotificacionesSuscripciones';
import ROSScoringConfig from './pages/ROSScoringConfig';
import ROSReports from './pages/ROSReports';
import ROSGenerarLote from './pages/ROSGenerarLote';
import RosConfig from './pages/RosConfig';
import ROSDetalle from './pages/ROSDetalle';
import NetworkAnalysis from './pages/NetworkAnalysis';
import ExecutionHistory from './pages/ExecutionHistory';
import VinculosAnalysis from './pages/VinculosAnalysis';
import MonitorScheduler from './pages/MonitorScheduler';
import ScheduledReports from './pages/ScheduledReports';
import InvestigacionesList from './pages/InvestigacionesList';
import InvestigacionNueva from './pages/InvestigacionNueva';
import InvestigacionDetalle from './pages/InvestigacionDetalle';
import InvestigacionComparar from './pages/InvestigacionComparar';
import MisCasos from './pages/MisCasos';
import ManualUsuario from './pages/ManualUsuario';
import PadronInterno from './pages/PadronInterno';
import ArcaConsulta from './pages/arca/ArcaConsulta';
import ArcaConfig from './pages/arca/ArcaConfig';
import ArcaApiExterna from './pages/arca/ArcaApiExterna';
import ArcaApoc from './pages/arca/ArcaApoc';
import ArcaWscdc from './pages/arca/ArcaWscdc';
import ArcaBuscarTodo from './pages/arca/ArcaBuscarTodo';
import ArcaHistorial from './pages/arca/ArcaHistorial';
import ArcaFacturacion from './pages/arca/ArcaFacturacion';
import ComplianceDashboard from './pages/ComplianceDashboard';
import AlertasCliente from './pages/AlertasCliente';
import SLADashboard from './pages/SLADashboard';
import CategoriasDocumentos from './pages/CategoriasDocumentos';
import OnboardingFieldsConfig from './pages/OnboardingFieldsConfig';
import EmpresasDashboard from './pages/EmpresasDashboard';
import GestionUsuarios from './pages/GestionUsuarios';
import OnboardingUnificado from './pages/OnboardingUnificado';
import ExportCenter from './pages/ExportCenter';
import RiskScoringClientes from './pages/RiskScoringClientes';
import AltasDashboard from './pages/AltasDashboard';
import MotorEvaluar from './pages/credito/MotorEvaluar';
import ReglasEditor from './pages/credito/ReglasEditor';
import Decisiones from './pages/credito/Decisiones';
import Rentabilidad from './pages/credito/Rentabilidad';
import SolicitudesCredito from './pages/credito/Solicitudes';
import MoraConsolidada from './pages/credito/MoraConsolidada';
import InventarioPrestamos from './pages/credito/InventarioPrestamos';
import PanelPrestamos from './pages/credito/PanelPrestamos';
import Cobranzas from './pages/credito/Cobranzas';
import ReportesRegulatorios from './pages/ReportesRegulatorios';
import BcraReport from './pages/BcraReport';
import ComisionesWaya from './pages/ComisionesWaya';
import PerfilTransaccional from './pages/PerfilTransaccional';
import ExportarMovimientos from './pages/ExportarMovimientos';
import VigilanciaCuentas from './pages/VigilanciaCuentas';
import ActividadUsuarios from './pages/ActividadUsuarios';
import ProcesosAutomaticos from './pages/ProcesosAutomaticos';
import { ActividadTracker } from './components/actividad/ActividadTracker';
import Seguridad from './pages/Seguridad';
import ReporteF8125 from './pages/ReporteF8125';
import DashboardF8125 from './pages/DashboardF8125';
import DashboardF8126 from './pages/DashboardF8126';
import NotificacionesConfig from './pages/NotificacionesConfig';
import AuditLogs from './pages/AuditLogs';
import Tramites from './pages/Tramites';
import TramiteDetalle from './pages/TramiteDetalle';
import TramitesDashboard from './pages/TramitesDashboard';
import TramitesRecurrentes from './pages/TramitesRecurrentes';
import Solicitudes from './pages/Solicitudes';
import SolicitudesConfig from './pages/SolicitudesConfig';
import SolicitudDetalle from './pages/SolicitudDetalle';
import PermisosConfigV2 from './pages/PermisosConfigV2';
import ForceChangePassword from './pages/ForceChangePassword';
import BrandingSettings from './pages/BrandingSettings';
import Reportes from './pages/Reportes';
import ImpuestoCheque from './pages/ImpuestoCheque';
import KpisHome from './pages/kpis/KpisHome';
import KpisIngresos from './pages/kpis/KpisIngresos';
import KpisAdquisicion from './pages/kpis/KpisAdquisicion';
import KpisAdopcion from './pages/kpis/KpisAdopcion';
import KpisClientesActivos from './pages/kpis/KpisClientesActivos';
import KpisEstado from './pages/kpis/KpisEstado';
import KpisTransaccionalidad from './pages/kpis/KpisTransaccionalidad';
import KpisBloqueos from './pages/kpis/KpisBloqueos';
import KpisAfiliacion from './pages/kpis/KpisAfiliacion';
import KpisGlosario from './pages/kpis/KpisGlosario';
import KpisMiTablero from './pages/kpis/KpisMiTablero';
import KpisPrestamos from './pages/kpis/KpisPrestamos';
import KpisCuentaRemunerada from './pages/kpis/KpisCuentaRemunerada';
import KpisAtencion from './pages/kpis/KpisAtencion';
import KpisPresentaciones from './pages/kpis/KpisPresentaciones';
import KpisConfiguracion from './pages/kpis/KpisConfiguracion';
import './App.css';

// Función para eliminar overlay amarillo de Emergent y asegurar colores correctos
const cleanEmergentOverlay = () => {
  const clean = () => {
    // 1. Limpiar SOLO elementos que tienen estilo inline de background amarillo/verde
    document.querySelectorAll('*').forEach(el => {
      const style = el.getAttribute('style');
      if (style && style.includes('background') && 
          (style.includes('255, 255, 0') || 
           style.includes('rgb(255, 255,') || 
           style.includes('yellow'))) {
        el.style.setProperty('background-color', 'transparent', 'important');
        el.style.setProperty('background', 'transparent', 'important');
      }
    });
    
    // 2. Para elementos con atributos de Emergent, SOLO limpiar si tienen background amarillo
    document.querySelectorAll('*[x-file-name], *[x-line-number], *[x-component], *[x-id], *[x-dynamic]').forEach(el => {
      const computedStyle = window.getComputedStyle(el);
      const bgColor = computedStyle.backgroundColor;
      if (bgColor && (bgColor.includes('255, 255, 0') || bgColor.includes('255, 255,'))) {
        el.style.setProperty('background-color', 'transparent', 'important');
        el.style.setProperty('background', 'transparent', 'important');
      }
    });

    // 3. FORCE correct colors on sidebar elements
    const sidebar = document.querySelector('div[style*="backgroundColor: rgb(15, 23, 42)"]') ||
                    document.querySelector('div[style*="background-color: rgb(15, 23, 42)"]');
    if (sidebar) {
      sidebar.style.setProperty('background-color', '#0f172a', 'important');
      sidebar.querySelectorAll('h1, span, p, button').forEach(el => {
        const inlineColor = el.style.color;
        if (inlineColor) {
          el.style.setProperty('color', inlineColor, 'important');
        }
      });
    }

    // 4. Force button backgrounds
    document.querySelectorAll('button').forEach(btn => {
      const inlineBg = btn.style.backgroundColor;
      if (inlineBg && inlineBg !== 'transparent') {
        btn.style.setProperty('background-color', inlineBg, 'important');
      }
      const inlineColor = btn.style.color;
      if (inlineColor) {
        btn.style.setProperty('color', inlineColor, 'important');
      }
    });

    // 5. Force colors on login page brand section
    document.querySelectorAll('div[style*="backgroundImage"] h1, div[style*="backgroundImage"] p, div[style*="backgroundImage"] svg').forEach(el => {
      const inlineColor = el.style.color;
      if (inlineColor) {
        el.style.setProperty('color', inlineColor, 'important');
      }
    });
  };
  
  // Run cleanup multiple times to catch dynamic changes
  clean();
  const interval = setInterval(clean, 150);
  setTimeout(() => clearInterval(interval), 8000);
};

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  useEffect(() => {
    cleanEmergentOverlay();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return user ? <>{children}<ActividadTracker /></> : <Navigate to="/login" />;
};

function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <ErrorBoundary>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/force-change-password" element={<ForceChangePassword />} />
          
          {/* Onboarding Portal (Public) */}
          <Route path="/onboarding/:token" element={<OnboardingPortal />} />
          
          {/* Module Home (Protected) */}
          <Route
            path="/"
            element={
              <PrivateRoute>
                <ModuleHome />
              </PrivateRoute>
            }
          />

          {/* Module Routes (Protected) */}
          <Route
            path="/m/:moduleSlug"
            element={
              <PrivateRoute>
                <ModuleLayout />
              </PrivateRoute>
            }
          >
            {/* Monitoreo Transaccional sections */}
            <Route path="dashboard" element={<DashboardRouter />} />
            <Route path="executive" element={<ExecutiveDashboard />} />
            <Route path="alerts" element={<Alerts />} />
            <Route path="alerts/:id" element={<AlertDetail />} />
            <Route path="cases" element={<CasesPage />} />
            <Route path="calendar" element={<AlertsCalendar />} />
            <Route path="monitors" element={<Monitors />} />
            <Route path="scheduler" element={<MonitorScheduler />} />
            <Route path="historial-ejecuciones" element={<ExecutionHistory />} />
            <Route path="dashboard-ejecuciones" element={<ExecutionHistory />} />
            <Route path="vinculos-analysis" element={<VinculosAnalysis />} />
            <Route path="templates" element={<MonitorTemplates />} />
            <Route path="links" element={<LinkAnalysisPage />} />
            <Route path="geo" element={<GeoHeatmap />} />
            <Route path="risk-scoring" element={<RiskScoring />} />
            <Route path="mrc" element={<MRCConfig />} />
            <Route path="account-lists" element={<AccountLists />} />
            <Route path="escalation" element={<EscalationConfig />} />
            <Route path="reports" element={<Reports />} />
            <Route path="audit" element={<Audit />} />
            
            {/* Investigaciones */}
            <Route path="investigaciones" element={<InvestigacionesList />} />
            <Route path="investigaciones/nueva" element={<InvestigacionNueva />} />
            <Route path="investigaciones/comparar" element={<InvestigacionComparar />} />
            <Route path="investigaciones/:id" element={<InvestigacionDetalle />} />
            
            {/* Gestión de Casos */}
            <Route path="mis-casos" element={<MisCasos />} />
            <Route path="casos" element={<CasesPage />} />
            <Route path="casos-kanban" element={<CasesKanban />} />
            <Route path="caso/:caseId" element={<CasoDetalle />} />
            <Route path="informe-editor" element={<InformeEditor />} />
            <Route path="preparar-vigilancia/:jobId" element={<PrepararVigilancia />} />
            <Route path="vigilancia" element={<VigilanciaCuentas />} />
            <Route path="analisis-vinculacion" element={<AnalisisVinculacion />} />
            <Route path="aprobaciones" element={<Aprobaciones />} />
            <Route path="network-analysis" element={<NetworkAnalysis />} />
            
            {/* Clientes sections */}
            <Route path="padron" element={<Navigate to="/m/clientes/padron-interno" replace />} />
            <Route path="padron-interno" element={<PadronInterno />} />
            <Route path="movimientos" element={<Movimientos />} />
            <Route path="cuentas-waya" element={<CuentasWaya />} />
            <Route path="coelsa" element={<CoelsaTransacciones />} />
            <Route path="waya-movimientos" element={<WayaMovimientos />} />
            <Route path="altas-dashboard" element={<AltasDashboard />} />
            <Route path="empresas-dashboard" element={<EmpresasDashboard />} />
            <Route path="empresas" element={<EmpresasPage />} />
            <Route path="crear-sucursal" element={<CrearSucursal />} />
            <Route path="empresa/:empresaId" element={<EmpresaDetalle />} />
            
            {/* Crédito (Motor de Decisión) */}
            <Route path="motor" element={<MotorEvaluar />} />
            <Route path="panel" element={<PanelPrestamos />} />
            <Route path="prestamos-solicitudes" element={<SolicitudesCredito />} />
            <Route path="inventario" element={<InventarioPrestamos />} />
            <Route path="decisiones" element={<Decisiones />} />
            <Route path="mora-consolidada" element={<MoraConsolidada />} />
            <Route path="rentabilidad" element={<Rentabilidad />} />
            <Route path="reglas" element={<ReglasEditor />} />
            <Route path="cobranzas" element={<Cobranzas />} />
            
            {/* Configuración sections */}
            <Route path="gestion-usuarios" element={<GestionUsuarios />} />
            <Route path="users" element={<UsersPage />} />
            <Route path="grupos" element={<GruposUsuarios />} />
            <Route path="plantillas-caso" element={<PlantillasCaso />} />
            <Route path="flujos-aprobacion" element={<FlujosAprobacionV2 />} />
            <Route path="alertas-cliente" element={<AlertasCliente />} />
            <Route path="sla-dashboard" element={<SLADashboard />} />
            <Route path="categorias-documentos" element={<CategoriasDocumentos />} />
            <Route path="onboarding-fields" element={<OnboardingFieldsConfig />} />
            <Route path="onboarding-unificado" element={<OnboardingUnificado />} />
            <Route path="escalamiento" element={<EscalationConfigV2 />} />
            <Route path="compliance-dashboard" element={<ComplianceDashboard />} />
            <Route path="onboarding-config" element={<OnboardingConfig />} />
            <Route path="solicitudes-onboarding" element={<SolicitudesOnboarding />} />
            <Route path="solicitudes-onboarding/:requestId" element={<OnboardingReview />} />
            <Route path="field-config" element={<FieldConfigPage />} />
            <Route path="module-admin" element={<ModuleAdmin />} />
            <Route path="bloqueos-freddy" element={<Seguridad />} />
            <Route path="credenciales-m2m" element={<Seguridad mode="credentials" />} />
            <Route path="actividad-usuarios" element={<ActividadUsuarios />} />
            <Route path="procesos-automaticos" element={<ProcesosAutomaticos />} />
            {/* permisos-config eliminado - integrado en gestion-usuarios */}
            <Route path="dashboard-config" element={<DashboardConfig />} />
            <Route path="estados-casos" element={<CaseStatusesAdmin />} />
            <Route path="casos-duplicados" element={<CaseDuplicates />} />
            <Route path="backlog" element={<BacklogCleanup />} />
            <Route path="system-settings" element={<SystemSettings />} />
            <Route path="branding" element={<BrandingSettings />} />
            <Route path="reportes" element={<Reportes />} />
            <Route path="impuesto-cheque" element={<ImpuestoCheque />} />
            <Route path="reportes-avanzados" element={<Reportes />} />
            <Route path="delegaciones" element={<Delegaciones />} />
            <Route path="historial-actividad" element={<HistorialActividad />} />
            <Route path="ros-scoring-config" element={<ROSScoringConfig />} />
            <Route path="ros-reports" element={<ROSReports />} />
            <Route path="ros-generar-lote" element={<ROSGenerarLote />} />
            <Route path="ros-config" element={<RosConfig />} />
            <Route path="ros-detalle/:rosId" element={<ROSDetalle />} />
            <Route path="scheduled-reports" element={<ScheduledReports />} />
            <Route path="manual" element={<ManualUsuario />} />
            <Route path="audit-logs" element={<AuditLogs />} />

            {/* Módulo ARCA (ex AFIP) */}
            <Route path="consulta" element={<ArcaConsulta />} />
            <Route path="buscar-todo" element={<ArcaBuscarTodo />} />
            <Route path="apoc" element={<ArcaApoc />} />
            <Route path="wscdc" element={<ArcaWscdc />} />
            <Route path="facturacion" element={<ArcaFacturacion />} />
            <Route path="arca-historial" element={<ArcaHistorial />} />
            <Route path="configuracion" element={<ArcaConfig />} />
            <Route path="api-externa" element={<ArcaApiExterna />} />
            
            {/* Módulo Trámites y Tareas */}
            <Route index element={<Tramites />} />
            <Route path="lista" element={<Tramites />} />
            <Route path="recurrentes" element={<TramitesRecurrentes />} />
            <Route path="solicitudes" element={<Solicitudes />} />
            <Route path="solicitudes/:solicitudId" element={<SolicitudDetalle />} />
            <Route path="solicitudes-config" element={<SolicitudesConfig />} />
            <Route path="dashboard" element={<TramitesDashboard />} />
            <Route path=":tramiteId" element={<TramiteDetalle />} />
            
            {/* Rutas legacy de trámites (por compatibilidad) */}
            <Route path="tramites" element={<Tramites />} />
            <Route path="tramites/lista" element={<Tramites />} />
            <Route path="tramites/recurrentes" element={<TramitesRecurrentes />} />
            <Route path="tramites/solicitudes" element={<Solicitudes />} />
            <Route path="tramites/solicitudes/:solicitudId" element={<SolicitudDetalle />} />
            <Route path="tramites/solicitudes-config" element={<SolicitudesConfig />} />
            <Route path="tramites/dashboard" element={<TramitesDashboard />} />
            <Route path="tramites/:tramiteId" element={<TramiteDetalle />} />
            
            {/* Módulo Reportes */}
            <Route path="dashboard-ejecutivo" element={<ExecutiveDashboard />} />
            <Route path="bcra" element={<BcraReport />} />
            <Route path="comisiones-waya" element={<ComisionesWaya />} />
            <Route path="perfil-transaccional" element={<PerfilTransaccional />} />
            <Route path="exportar-movimientos" element={<ExportarMovimientos />} />
            <Route path="export-center" element={<ExportCenter />} />
            <Route path="reportes-regulatorios" element={<ReportesRegulatorios />} />
            <Route path="f8125" element={<ReporteF8125 />} />
            <Route path="dashboard-f8125" element={<DashboardF8125 />} />
            <Route path="dashboard-f8126" element={<DashboardF8126 />} />
            
            {/* Módulo Clientes - Scoring Avanzado */}
            <Route path="risk-scoring-clientes" element={<RiskScoringClientes />} />

            {/* Módulo Tablero Ejecutivo (KPIs) */}
            <Route path="kpis-mi-tablero" element={<KpisMiTablero />} />
            <Route path="kpis-home" element={<KpisHome />} />
            <Route path="kpis-ingresos" element={<KpisIngresos />} />
            <Route path="kpis-adquisicion" element={<KpisAdquisicion />} />
            <Route path="kpis-adopcion" element={<KpisAdopcion />} />
            <Route path="kpis-clientes-activos" element={<KpisClientesActivos />} />
            <Route path="kpis-estado" element={<KpisEstado />} />
            <Route path="kpis-transaccionalidad" element={<KpisTransaccionalidad />} />
            <Route path="kpis-bloqueos" element={<KpisBloqueos />} />
            <Route path="kpis-afiliacion" element={<KpisAfiliacion />} />
            <Route path="kpis-prestamos" element={<KpisPrestamos />} />
            <Route path="kpis-cuenta-remunerada" element={<KpisCuentaRemunerada />} />
            <Route path="kpis-atencion" element={<KpisAtencion />} />
            <Route path="kpis-presentaciones" element={<KpisPresentaciones />} />
            <Route path="kpis-glosario" element={<KpisGlosario />} />
            <Route path="kpis-configuracion" element={<KpisConfiguracion />} />
            
            {/* Módulo Notificaciones */}
            <Route path="inbox" element={<NotificacionesInbox />} />
            <Route path="admin" element={<NotificacionesAdmin />} />
            <Route path="suscripciones" element={<NotificacionesSuscripciones />} />
            <Route path="notificaciones" element={<NotificacionesConfig />} />
            
            {/* Configuración de Permisos Granular */}
            <Route path="permisos-v2" element={<PermisosConfigV2 />} />
            
            {/* Cliente detail (shared) */}
            <Route path="cliente/:cuit" element={<ClienteDetalle />} />
          </Route>

          {/* Legacy Routes - Redirect to new structure */}
          <Route path="/dashboard" element={<Navigate to="/m/monitoreo-transaccional/dashboard" replace />} />
          
          {/* Manual de Usuario - Accesible desde cualquier parte */}
          <Route path="/manual" element={<ManualUsuario />} />
          <Route path="/ayuda" element={<ManualUsuario />} />
          
          <Route path="/alerts" element={<Navigate to="/m/monitoreo-transaccional/alerts" replace />} />
          <Route path="/alerts/:id" element={<Navigate to="/m/monitoreo-transaccional/alerts/:id" replace />} />
          <Route path="/monitors" element={<Navigate to="/m/monitoreo-transaccional/monitors" replace />} />
          <Route path="/monitor-templates" element={<Navigate to="/m/monitoreo-transaccional/templates" replace />} />
          <Route path="/movimientos" element={<Navigate to="/m/clientes/movimientos" replace />} />
          <Route path="/padron" element={<Navigate to="/m/clientes/padron-interno" replace />} />
          <Route path="/cliente/:cuit" element={<Navigate to="/m/clientes/cliente/:cuit" replace />} />
          <Route path="/audit" element={<Navigate to="/m/monitoreo-transaccional/audit" replace />} />
          <Route path="/users" element={<Navigate to="/m/configuracion/users" replace />} />
          <Route path="/reports" element={<Navigate to="/m/monitoreo-transaccional/reports" replace />} />
          <Route path="/risk-scoring" element={<Navigate to="/m/monitoreo-transaccional/risk-scoring" replace />} />
          <Route path="/calendar" element={<Navigate to="/m/monitoreo-transaccional/calendar" replace />} />
          <Route path="/account-lists" element={<Navigate to="/m/monitoreo-transaccional/account-lists" replace />} />
          <Route path="/escalation" element={<Navigate to="/m/monitoreo-transaccional/escalation" replace />} />
          <Route path="/mrc-config" element={<Navigate to="/m/monitoreo-transaccional/mrc" replace />} />
          <Route path="/executive" element={<Navigate to="/m/monitoreo-transaccional/executive" replace />} />
          <Route path="/cases" element={<Navigate to="/m/monitoreo-transaccional/cases" replace />} />
          <Route path="/links" element={<Navigate to="/m/monitoreo-transaccional/links" replace />} />
          <Route path="/geo-heatmap" element={<Navigate to="/m/monitoreo-transaccional/geo" replace />} />
          <Route path="/branding" element={<Navigate to="/m/configuracion/branding" replace />} />

          {/* Catch all - redirect to home */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
